import { forwardRef } from 'react';
import { breakpoints } from '#breakpoints/constants.ts';
import type { Prefixed } from './theme/stitches.config';
import type { GDSStyleObject, GDSSpace, GDSSize } from './theme';
import { Icon } from './components/icon/icon';
import { IconEmoji } from './components/icon/icon-emoji';

// React.forwardRef doesn't support generic (polymorphic) components.
// This wrapper bypasses its parameter type checking while preserving
// runtime behavior. Type safety comes from the annotation at the call site.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const polymorphicForwardRef = forwardRef as any;

export interface GDSCustomizableComponent {
  className?: string;

  /**
   * Optional object to provide styles to the component
   */
  css?: GDSStyleObject;

  /**
   * Optional identifier used for testing
   */
  'data-testid'?: string;
}

export interface GDSComponent {
  /**
   * Specify a custom `ref`
   */
  ref?: any;
}

export interface GDSHTMLAttributes {
  /**
   * Specify a custom `id`
   */
  id?: string;
}

export type GDSOrientationValues = 'horizontal' | 'vertical';

export type GDSHorizontalAlignmentValues = 'start' | 'center' | 'end';

export type GDSVerticalAlignmentValues = 'top' | 'center' | 'bottom';

export type GDSPosition = 'leading' | 'trailing';

export interface GDSOrientation {
  /**
   * Set the orientation
   */
  orientation?: GDSOrientationValues;
}

export type GDSTypographyIntrinsicEmphasis = 'base' | 'subtle';

// Polymorphic component types based on https://blog.logrocket.com/build-strongly-typed-polymorphic-components-react-typescript/
type AsProp<C extends React.ElementType> = {
  as?: C;
};

type PropsToOmit<C extends React.ElementType, P> = keyof (AsProp<C> & P);

export type PolymorphicRef<C extends React.ElementType> =
  React.ComponentPropsWithRef<C>['ref'];

export type PolymorphicComponentProp<
  C extends React.ElementType,
  Props = {},
> = React.PropsWithChildren<Props & AsProp<C>> &
  Omit<React.ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;

export type PolymorphicComponentPropWithRef<
  C extends React.ElementType,
  Props = {},
> = PolymorphicComponentProp<C, Props> & { ref?: PolymorphicRef<C> };

export type AnyStringOrObject = (string & {}) | object;
export type GDSSpacingValues = GDSSpace | AnyStringOrObject;
export type GDSSizingValues = GDSSize | AnyStringOrObject;

//
export type GDSResponsivePropByBreakpoint<T> = Required<{ '@initial': T }> &
  Partial<Record<Prefixed<'@', keyof typeof breakpoints>, T>>;

// this below doesn't include `@initial`. Also need to fine a way to `@initial` required and remaining
// breakpoint names optional
export type GDSResponsiveProp<T> = T | GDSResponsivePropByBreakpoint<T>;

export type GDSBreakpoints = typeof breakpoints;
export type GDSBreakpoint = keyof typeof breakpoints;
export type GDSBreakpointOrNumber = GDSBreakpoint | number;

export type GDSStatus =
  | 'info'
  | 'success'
  | 'error'
  | 'warning'
  | 'neutral'
  | 'brand';

export type GDSInputStatus = Extract<
  GDSStatus,
  'success' | 'warning' | 'error'
>;

export type GDSImagePosition = 'top' | 'start' | 'end';

export type GDSIcon = typeof Icon;

export type GDSEmoji = typeof IconEmoji;

export type GDSAlign = 'start' | 'center' | 'end';
export type GDSSide =
  | 'top'
  | 'right'
  | 'inlineEnd'
  | 'bottom'
  | 'left'
  | 'inlineStart';

export type GDSTrailingIcon =
  | 'externalLink'
  | 'rightChevron'
  | 'meatballs'
  | 'newWindowInternal';

export type GDSBackgroundColor = 'primary' | 'secondary' | 'tertiary';
